import { useState } from 'react';
// Import Shared Utils
import { post } from 'shared-components/src/utils/http';
// Import Local Utils
import { cardFieldError } from '../utils/validationHandlers';
import { digitsOnly, expandExpiry } from '../utils/cardFormatting';

export const ACTIVATION_PHASES = {
  FORM: 'form',
  SUBMITTING: 'submitting',
  ACTIVATED: 'activated',
  ALREADY_ACTIVE: 'already_active',
};

const CARD_FIELDS = ['cardNumber', 'expiry', 'cvv'];
const EMPTY_CARD = { cardNumber: '', cvv: '', expiry: '' };
const NO_ERRORS = { cardNumber: '', cvv: '', expiry: '' };
const NO_CONSENTS = {
  electronicCom: false,
  terms: false,
  cardholderAgreement: false,
  privacyPolicy: false,
};

// Activation codes that belong to one field, as the activation page maps them.
const FIELD_FOR_CODE = {
  invalid_card_number: 'cardNumber',
  card_not_activated: 'cardNumber',
  failed_to_verify_card: 'cvv',
  failed_to_verify_card_cvv: 'cvv',
  failed_to_verify_card_expiry_date: 'expiry',
  failed_to_verify_card_expired: 'expiry',
};

// Card activation from the dashboard's dialog, through the same public endpoint as the
// activation page: the card's number, expiry and CVV are the proof, so it works signed in.
// onActivated runs once the cardholder leaves the success screen, so the page can re-read
// the cardholder (activation ends an in-progress replacement).
export default ({ onActivated } = {}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState(ACTIVATION_PHASES.FORM);
  const [card, setCard] = useState(EMPTY_CARD);
  const [errors, setErrors] = useState(NO_ERRORS);
  const [consents, setConsents] = useState(NO_CONSENTS);
  // An i18n key, or the server's message when it has no key.
  const [formError, setFormError] = useState(null);

  const reset = () => {
    setPhase(ACTIVATION_PHASES.FORM);
    setCard(EMPTY_CARD);
    setErrors(NO_ERRORS);
    setConsents(NO_CONSENTS);
    setFormError(null);
  };

  const open = () => {
    reset();
    setIsOpen(true);
  };

  const close = () => {
    const activated = phase === ACTIVATION_PHASES.ACTIVATED
      || phase === ACTIVATION_PHASES.ALREADY_ACTIVE;
    setIsOpen(false);
    if (activated && onActivated) onActivated();
  };

  const change = (key, value) => setCard(current => ({ ...current, [key]: value }));
  const blur = key => setErrors(current => ({ ...current, [key]: cardFieldError(key, card) }));
  const consent = (key, value) => setConsents(current => ({ ...current, [key]: value }));

  const refuse = (error, messages) => {
    const code = (error && error.code) || 'unknown_error';
    const key = messages[`errors-${code}`] ? `errors-${code}` : null;
    const field = FIELD_FOR_CODE[code];
    if (field) {
      setErrors(current => ({ ...current, [field]: key || error.message }));
    } else {
      setFormError(key || (error && error.message) || 'errors-card_activation_failed');
    }
    // The consents stay ticked: the shared Checkbox is uncontrolled, so clearing them here
    // would leave a ticked box with Activate disabled.
    setPhase(ACTIVATION_PHASES.FORM);
  };

  // messages is intl.messages, to tell a known error code from a server message.
  const submit = async messages => {
    const found = {};
    CARD_FIELDS.forEach(key => {
      found[key] = cardFieldError(key, card);
    });
    setErrors(found);
    if (CARD_FIELDS.some(key => found[key])) return;

    const expiry = expandExpiry(card.expiry);
    setFormError(null);
    setPhase(ACTIVATION_PHASES.SUBMITTING);
    try {
      const res = await post('/api/v1/cardholder_portal/activate_card', {
        card_number: digitsOnly(card.cardNumber),
        cvv: card.cvv,
        expiry_month: expiry.slice(0, 2),
        expiry_year: expiry.slice(2),
      });
      if (res.error) {
        refuse(res.error, messages);
        return;
      }
      setPhase(
        res.data && res.data.code === 'already_activated'
          ? ACTIVATION_PHASES.ALREADY_ACTIVE
          : ACTIVATION_PHASES.ACTIVATED
      );
    } catch (e) {
      refuse(e, messages);
    }
  };

  return {
    isOpen,
    phase,
    card,
    errors,
    consents,
    formError,
    open,
    close,
    change,
    blur,
    consent,
    submit,
  };
};
